import React, { FC } from "react";

import { navigate as navigate_ } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";
import { useFocusRing } from "react-aria";

import { type SerializedStyles, css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { navigate } from "../../../hooks/navigation";
import Link from "../../base/atoms/Link";
import { Theme } from "../../Theme";
import { IconPredefined } from "../icons/Icon";
import { PredefinedIconType } from "../icons/Icon/Predefined";

export {
    /** @deprecated Use hooks/navigation instead */
    navigate,
    /** @deprecated Use hooks/navigation instead */
    useHref,
    /** @deprecated Use hooks/navigation instead */
    useSameOriginHref,
    /** @deprecated Use hooks/navigation instead */
    useSameOriginHrefAbs,
} from "../../../hooks/navigation";
export {
    /** @deprecated Use components/base/atoms/Link instead */
    default as Link,
} from "../../base/atoms/Link";
export {
    /** @deprecated Use components/base/atoms/Redirect instead */
    default as Redirect,
} from "../../base/atoms/Redirect";

const StyledLink = styled(Link)`
    display: flex;
    column-gap: 5px;
    align-items: center;
    text-decoration: underline;
`;

const ButtonLink = styled(StyledLink)`
    width: 100%;
    padding: 10px 20px;
    font-size: 1em;
    cursor: pointer;
    background-color: transparent;
    border-style: solid;
    border-radius: 7px;

    ${({ theme }) => theme.breakpoints.from.medium.css`
        width: fit-content;
        padding: 10px 50px;
    `}
`;

const StyledButton = styled.button`
    display: flex;
    column-gap: 5px;
    align-items: center;
    background-color: unset;
    border: none;
    text-decoration: underline;
`;

type CMSLink = {
    href: string;
    text: string;
};

type LinkComponentProps = {
    link: CMSLink;
    linkItemCss?: SerializedStyles;
    // font
    bold?: boolean;
    extraBold?: boolean;
    fontSize?: string;
    fSExtraSmall?: string;
    fSSmall?: string;
    /** @deprecated */
    fsMedium?: string;
    fSMedium?: string;
    fSNormal?: string;
    fSLarge?: string;
    fSExtraLarge?: string;
    underline?: boolean;
    bottomBorder?: boolean;
    fullWidth?: boolean;
} & (
    | { noLink?: false; button?: false; goBack?: false }
    | { noLink: true; button?: false; goBack?: false }
    | { noLink?: false; button: true; goBack?: false }
    | { noLink?: false; button?: false; goBack: true }
) &
    (
        | {
              icon?: null;
              leftIcon?: undefined;
              moveIcon?: undefined;
              iconSize?: undefined;
              iSSmall?: undefined;
              iSExtraSmall?: undefined;
          }
        | {
              icon: PredefinedIconType;
              leftIcon?: boolean;
              moveIcon?: boolean;
              iconSize?: string;
              iSSmall?: string;
              iSExtraSmall?: string;
          }
    );

const LinkComponent = ({
    link,
    linkItemCss,
    noLink = false,
    button = false,
    goBack = false,
    bold = true,
    extraBold = false,
    fontSize = "1rem",
    fSExtraSmall = fontSize,
    fSSmall = fontSize,
    fsMedium = fontSize,
    fSMedium = fsMedium,
    fSNormal = fontSize,
    fSLarge = fontSize,
    fSExtraLarge = fontSize,
    underline = true,
    bottomBorder = false,
    icon,
    leftIcon = false,
    moveIcon = true,
    iconSize = "25px",
    iSSmall = iconSize,
    iSExtraSmall = iconSize,
    fullWidth = false,
    ...props
}: LinkComponentProps) => {
    const theme = useTheme();
    const location = useLocation();

    const { isFocusVisible, focusProps } = useFocusRing();

    const colorCode = theme.colors.blue?.toString() ?? "#05235f";
    const activeColor = theme.colors.darkBlue?.toString() ?? "#04132e";

    const focusStyle = isFocusVisible
        ? {
              outline: `2px solid ${colorCode}`,
              outlineOffset: 6,
              borderRadius: 4,
          }
        : {};

    const linkCss = css`
        column-gap: 10px;
        font-size: ${fontSize};
        font-weight: ${bold ? 600 : "inherit"};
        color: ${colorCode} !important;

        ${theme.breakpoints.propPerEachFrom("font-size", {
            extraLarge: fSExtraLarge,
            largeMedium: fSLarge,
            large: fSLarge,
            normal: fSNormal,
            medium: fSMedium,
            small: fSSmall,
            extraSmall: fSExtraSmall,
        })}

        ${fullWidth &&
        css`
            justify-content: space-between;
            width: 100% !important;
        `}
    `;

    const stateStyles = css`
        ${theme.selectors.hover.css`
            text-decoration: underline;
            color: ${colorCode};
        `}

        &:active {
            text-decoration: underline;
            color: ${activeColor};
        }

        &:visited {
            text-decoration: underline;
            color: #70508d !important;
        }

        &[disabled],
        &.disabled {
            color: #878787;
            pointer-events: none;
            text-decoration: underline;
        }
    `;

    const styledLinkCss = css`
        ${linkCss}
        ${linkItemCss && linkItemCss}

        text-decoration: ${underline ? "underline" : "inherit"};

        ${bottomBorder &&
        css`
            border-bottom: 2px solid ${colorCode};
        `}

        ${stateStyles}
    `;

    const buttonLinkCss = css`
        ${linkCss};
        justify-content: center;
        border: 2px solid ${colorCode};
        ${stateStyles}
    `;

    const iconStyleRight = css`
        position: relative;
        left: 0;
        order: ${leftIcon ? 1 : 2};
        min-width: ${iconSize};
        transition: all 250ms ease-out;
    `;

    const iconStyleLeft = css`
        position: relative;
        right: 0;
        order: ${leftIcon ? 1 : 2};
        transition: all 250ms ease-out;
    `;

    const linkContentCss = css`
        order: ${leftIcon ? 2 : 1};
    `;

    const boldCss = css`
        font-weight: 900;
    `;

    const handleGoBack = () => {
        if ((location.state as any)?.from) {
            void navigate_(-1);
        } else {
            void navigate(link.href);
        }
    };

    const content = (
        <>
            <span
                css={css`
                    ${linkContentCss}
                    ${extraBold && boldCss}
                `}
            >
                {link.text}
            </span>

            {icon && (
                <IconPredefined<PredefinedIconType>
                    type={icon}
                    color="currentColor"
                    size={iconSize}
                    sSmall={iSSmall}
                    sExtraSmall={iSExtraSmall}
                    css={leftIcon ? iconStyleLeft : iconStyleRight}
                />
            )}
        </>
    );

    if (noLink) {
        const noLinkCss = css`
            display: flex;
            column-gap: 5px;
            align-items: center;
            ${styledLinkCss}

            &:hover {
                cursor: pointer;
            }
        `;
        return (
            <div css={noLinkCss} {...focusProps} style={focusStyle} {...props}>
                {content}
            </div>
        );
    }

    if (button) {
        return (
            <ButtonLink
                to={link.href}
                css={buttonLinkCss}
                {...focusProps}
                style={focusStyle}
                {...props}
            >
                {content}
            </ButtonLink>
        );
    }

    if (goBack) {
        return (
            <StyledButton
                onClick={handleGoBack}
                css={styledLinkCss}
                {...focusProps}
                style={focusStyle}
                {...props}
            >
                <span
                    css={css`
                        ${linkContentCss}
                        ${extraBold && boldCss}
                    `}
                >
                    Zurück
                </span>

                {icon && (
                    <IconPredefined<PredefinedIconType>
                        type={icon}
                        color={colorCode}
                        size={iconSize}
                        sSmall={iSSmall}
                        sExtraSmall={iSExtraSmall}
                        css={leftIcon ? iconStyleLeft : iconStyleRight}
                    />
                )}
            </StyledButton>
        );
    }

    return (
        <StyledLink
            to={link.href}
            css={styledLinkCss}
            state={{ from: location.pathname }}
            {...focusProps}
            style={focusStyle}
            {...props}
        >
            {content}
        </StyledLink>
    );
};

export default LinkComponent;
